// Schema rules shared by the templates, parameters and enums pages (spec/dashboard.md section 5) and by the editors:
// the API's name patterns, the user-facing type and flag catalogues, the defaults per type.
import type { EnumDefinition, Json, Parameter, Template } from '../api/types';

export type ParamType = Parameter['type'];
export type TemplateKind = Template['kind'];

/** The user-facing parameter types in the drawer's order (research/02 section 5 → Huelency types). */
export const PARAM_TYPES: { value: ParamType; label: string; hint?: string }[] = [
  { value: 'int', label: 'Integer' },
  { value: 'long', label: 'Long', hint: 'kept exact beyond 2^53; edited as text' },
  { value: 'float', label: 'Float' },
  { value: 'bool', label: 'Boolean' },
  { value: 'string', label: 'String', hint: 'an optional regex is checked when a value commits' },
  { value: 'localized_string', label: 'Localized string', hint: 'a string of the localization table; released as GROUP/KEY' },
  { value: 'enum', label: 'Enum' },
  { value: 'date_time', label: 'Date time', hint: 'unix seconds, shown in the display zone' },
  { value: 'duration', label: 'Duration', hint: 'seconds, edited as D H M S' },
  { value: 'color', label: 'Color', hint: '#RRGGBBAA' },
  { value: 'document', label: 'Document / Component', hint: 'a reference to a document of the target template or its heirs; a component template makes it an owned inline component' },
  { value: 'asset', label: 'Asset' },
  { value: 'product', label: 'Product', hint: '{ productId, amount, currency }' },
  { value: 'object', label: 'Object (inline)', hint: 'an embedded object whose fields are the inline template’s parameters' },
  { value: 'list', label: 'List' },
  { value: 'template_ref', label: 'Template', hint: 'a template name (profile paths)' },
  { value: 'profile_path', label: 'Profile path', hint: 'a dotted path into a profile, GeneralInfo.Level' },
  { value: 'profile_data', label: 'Profile data', hint: 'a nested profile structure; only on profile templates' },
  { value: 'any', label: 'Any (JSON)', hint: 'untyped JSON kept for imported values' },
];

export const TYPE_LABEL = new Map(PARAM_TYPES.map((t) => [t.value, t.label]));

/** The asset kinds (research/10 section 2.2; the server's AssetKinds.All). */
export const ASSET_KINDS = ['image', 'ui_object', 'addressable', 'addressables', 'view', 'font', 'audio', 'json', 'other', 'javascript', 'editor_script'];

/** Types a list may hold (no list of lists, no color — research/02 section 3.4). */
export const LIST_ELEMENT_TYPES: ParamType[] = ['int', 'long', 'float', 'bool', 'string', 'localized_string', 'enum', 'date_time', 'duration', 'document', 'asset', 'product', 'object', 'template_ref', 'profile_path', 'any'];

/** Types that may carry `isUnique` (research/02 section 3.4). */
export const UNIQUE_TYPES = new Set<ParamType>(['int', 'long', 'float', 'string', 'color']);

export const TEMPLATE_FLAGS: { value: string; label: string; help: string }[] = [
  { value: 'abstract', label: 'Abstract', help: 'The generated class is abstract and no document of this template can be created.' },
  { value: 'partial', label: 'Partial', help: 'The generated class is partial (the documented way to extend it in the game).' },
  { value: 'no_code', label: 'No code', help: 'No class and no accessor are generated.' },
  { value: 'no_class_file', label: 'No class file', help: 'The class file is skipped; the accessor is still generated.' },
  { value: 'cant_inherit', label: 'Can’t inherit', help: 'Hidden from the base-template picker.' },
  { value: 'hide_from_navigation', label: 'Hide from navigation', help: 'No sidebar entry under Documents.' },
  { value: 'internal', label: 'Internal', help: 'Visible only inside its namespace.' },
  { value: 'hide_id', label: 'Hide id', help: 'The id is left out of the document label.' },
  { value: 'static_in_clones', label: 'Static in clones', help: 'A package update replaces the copy wholesale.' },
  { value: 'generate_as_enum', label: 'Generate as enum', help: 'Also generates an enum listing every document.' },
  { value: 'no_release', label: 'No release', help: 'No release file: the documents never ship to clients.' },
  { value: 'package_private', label: 'Package private', help: 'Like Internal, scoped to the package.' },
];

export const PARAMETER_FLAGS: { value: string; label: string; help: string }[] = [
  { value: 'no_code', label: 'No code', help: 'No field is generated for it.' },
  { value: 'no_class_file', label: 'No class file', help: 'The class file is skipped; the accessor is still generated.' },
  { value: 'no_release', label: 'No release', help: 'The value is kept in git and left out of every release (the legacy "don’t generate on deploy").' },
  { value: 'as_injected', label: 'As injected', help: 'Legacy: treated as an injected component by old generators.' },
  { value: 'static_in_clones', label: 'Static in clones', help: 'A package update replaces the value wholesale.' },
  { value: 'package_private', label: 'Package private', help: 'Visible only inside its package.' },
];

export const TEMPLATE_NAME = /^[A-Z][A-Za-z0-9_]{0,63}(\.[A-Z][A-Za-z0-9_]{0,63}){0,7}$/;
export const PARAMETER_NAME = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
export const ENUM_NAME = TEMPLATE_NAME;

/** Namespaces the system templates own (the server stores them; a game’s own template must not claim them — research/02 section 3.2). */
export const RESERVED_PREFIXES = ['Unny', 'UnnySystem', 'System', 'SmartObjects', 'LiveOps', 'Core', 'SmartOffers', 'VisualScripting', 'Notifications', 'OfferGroupInfo'];

export function templateNameError(name: string, opts: { allowReserved?: boolean } = {}): string | null {
  if (!name) return 'A name is required.';
  if (!TEMPLATE_NAME.test(name)) return 'Letters, digits and underscores; every segment starts with a capital letter; dots separate namespaces (Hue.Items.Sword).';
  if (!opts.allowReserved) {
    const first = name.split('.')[0]!;
    const reserved = RESERVED_PREFIXES.find((p) => first.toLowerCase() === p.toLowerCase());
    if (reserved) return `${reserved} is a system namespace; pick another first segment.`;
  }
  return null;
}

export function parameterNameError(name: string): string | null {
  if (!name) return 'A name is required.';
  if (!PARAMETER_NAME.test(name)) return 'Letters, digits and underscores, not starting with a digit (maxStack, HCPrice).';
  return null;
}

/** "Hue.HueComponent" → "Hue Component": the last segment split on CamelCase (research/02 section 3.2). */
export function displayNameFrom(name: string): string {
  const last = name.split('.').pop() ?? '';
  return last.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2').trim();
}

/** The compiled regex of a string parameter, or the error of an invalid pattern — never a throw (Q-D-1). */
export function regexError(pattern: string): string | null {
  if (!pattern) return null;
  try {
    new RegExp(pattern);
    return null;
  } catch (e) {
    return e instanceof Error ? e.message.replace(/^Invalid regular expression: /, '') : 'invalid pattern';
  }
}

export function typeDefault(p: Parameter): Json {
  if (p.default !== null && p.default !== undefined) return p.default as Json;
  switch (p.type) {
    case 'int':
    case 'float':
    case 'duration':
    case 'date_time':
    case 'enum':
      return 0;
    case 'long':
      return '0';
    case 'bool':
      return false;
    case 'string':
    case 'profile_path':
      return '';
    case 'color':
      return '#FFFFFFFF';
    case 'list':
      return [];
    default:
      return null;
  }
}

/** The type as the parameter table shows it: `Enum: Hue Enum`, `Document → Item`, `List[Hue Component]`, `Asset: image`. */
export function typeLabel(p: Pick<Parameter, 'type' | 'of' | 'template' | 'enum' | 'assetKinds'>, enums?: Map<string, EnumDefinition>, templates?: Map<string, Template>): string {
  const short = (name: string | null | undefined) => (name ? (templates?.get(name)?.displayName ?? name.split('.').pop() ?? name) : '');
  switch (p.type) {
    case 'enum':
      return `Enum: ${p.enum ? (enums?.get(p.enum)?.displayName ?? p.enum.split('.').pop()) : '?'}`;
    case 'document':
      return p.template ? `${templates?.get(p.template)?.kind === 'component' ? 'Component' : 'Document'} → ${short(p.template)}` : 'Document (any)';
    case 'object':
      return p.template ? `Object: ${short(p.template)}` : 'Object';
    case 'profile_data':
      return p.template ? `Profile: ${short(p.template)}` : 'Profile data';
    case 'asset':
      return p.assetKinds && p.assetKinds.length > 0 ? `Asset: ${p.assetKinds.join(', ')}` : 'Asset';
    case 'list':
      return p.of ? `List[${typeLabel(p.of, enums, templates)}]` : 'List';
    default:
      return TYPE_LABEL.get(p.type) ?? p.type;
  }
}

export const KIND_LABEL: Record<TemplateKind, string> = {
  document: 'Document',
  component: 'Component',
  singleton: 'Singleton',
  inline: 'Inline',
  profile: 'Profile',
  profile_root: 'Profile root',
};

export function isPowerOfTwo(value: number | string): boolean {
  const n = typeof value === 'string' ? Number(value) : value;
  return Number.isInteger(n) && n > 0 && (n & (n - 1)) === 0;
}

/** The next value for "+ Add value": +1, or the next unused power of two for a flags enum (research/02 section 3.5). */
export function nextEnumValue(values: { value: number | string }[], isFlags: boolean): number {
  const numbers = values.map((v) => Number(v.value)).filter((n) => Number.isFinite(n));
  if (!isFlags) return numbers.length ? Math.max(...numbers) + 1 : 0;
  let bit = 1;
  while (numbers.includes(bit)) bit *= 2;
  return bit;
}

/** Templates a parameter of the given type may point at. */
export function targetKinds(type: ParamType): TemplateKind[] {
  switch (type) {
    case 'document':
      return ['document', 'singleton', 'component'];
    case 'object':
      return ['inline'];
    case 'profile_data':
      return ['profile', 'profile_root'];
    default:
      return [];
  }
}

/** Templates offered as a base: not inline / profile kinds, not `cant_inherit`, and of a kind the child's kind may extend. */
export function canBeBaseOf(base: Template, kind: TemplateKind): boolean {
  if (base.flags.includes('cant_inherit')) return false;
  if (base.kind === 'inline' || base.kind === 'profile' || base.kind === 'profile_root') return false;
  if (kind === 'component') return base.kind === 'component';
  return base.kind === 'document' || base.kind === 'singleton' || base.kind === 'component';
}

/** Whether a document template is one of the visual-scripting script templates (Q-D-18). */
export function isScriptTemplate(name: string | null | undefined): boolean {
  return !!name && /^VisualScripting\.|Script$/.test(name);
}
